// Cold-start hydration of the versions map from KV. After a hard reload
// the IndexedDB-restored session has a `sessionFolder` but its `versions`
// field can be stale (or empty if the user never re-saved after the last
// upload). Vanilla refreshes the map from the server right after setting
// `S.sessionFolder` — see `legacy/index.html:3354-3355`:
//
//   S.sessionFolder = saved.sessionFolder;
//   await fetchVersions();
//   await restoreSession(saved);
//
// In the React app the equivalent moment is "after `useSessionPersistence`
// has applied the stored session". We can't hook into that callback
// cleanly, but the side-effect-driven equivalent is a useEffect keyed on
// `(toolId, sessionFolder)` — it fires exactly once per session-folder
// per mount, which is what we want. Errors degrade gracefully (matches
// vanilla's `console.warn`-and-empty-map fallback at line 3239-3240).

import { useEffect } from 'react';
import { fetchVersions } from '@/lib/api';
import { useSessionStore } from '@/stores/session';

export function useVersionsHydration(
  toolId: string | null | undefined,
  sessionFolder: string,
): void {
  useEffect(() => {
    if (!toolId || !sessionFolder) return;
    let cancelled = false;
    fetchVersions(toolId, sessionFolder)
      .then((versions) => {
        if (cancelled) return;
        // Replace wholesale — KV is the source of truth on cold start.
        // Mirrors `S.versions = data.versions || {}` in legacy:3237.
        useSessionStore.getState().setVersions(versions);
      })
      .catch((err) => {
        // Vanilla swallows the error and leaves S.versions untouched
        // (effectively empty on cold start). Don't toast — VersionsBar
        // simply renders no pills, which is the documented degraded UX.
        console.warn('[versions] hydration failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [toolId, sessionFolder]);
}
